import React, { useMemo } from 'react';
import { Button, Dialog, DialogTitle, DialogActions, DialogContent, Box, Typography, Alert, Tooltip } from '@mui/material';
import { DataGrid, gridClasses, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExportContainer, GridCsvExportMenuItem } from '@mui/x-data-grid';
import { buildResistanceGeneRows } from '../AMRPredictions/resistanceGeneUtils';
import WarningIcon from '@mui/icons-material/Warning';
import { useResistanceGenes } from '../services/api/KeynomeAPIHooks';

const ResistanceGenesDialog = ({ isIllumina, sample, identifiedSpecies, open, onClose }) => {
  const { data: resistanceGenes, isLoading: resistanceGenesLoading } = useResistanceGenes(
    identifiedSpecies
      ?.filter(species => species.attributes.species_call_positive)
      ?.map(species => species.id)
  );
  const isPolymicrobial = (identifiedSpecies || []).filter(species => species.attributes.species_call_positive).length > 1;
  const filterOutResGenesWithQualityLimitations = isPolymicrobial || isIllumina;

  const resistanceGeneResult = useMemo(() => {
    if (!!resistanceGenesLoading || !!!resistanceGenes) {
      return {
        rows: [],
        showKgASTQualityWarning: false,
        hasFilteredResistanceGenes: false,
      };
    }

    return buildResistanceGeneRows({ identifiedSpecies, resistanceGenes, isIllumina });
  }, [identifiedSpecies, resistanceGenes, resistanceGenesLoading, isIllumina]);

  const { rows, showKgASTQualityWarning } = resistanceGeneResult;

  const columns = useMemo(() => [
    { field: 'organism',
      headerName: 'Organism',
      flex: 1 ,
      renderCell: (params) => {
        return <span>
          {params.value}
          {params.row.unclassifiedDuplicateMarker && <b style={{ fontSize: '15px', fontStyle: 'bold', color: 'orange' }}>*</b>}
        </span>
      }
    },
    { field: 'drugClass', headerName: 'Drug Class', flex: 1 },
    { 
      field: 'gene', 
      headerName: 'Resistance Marker', 
      flex: 1, 
      renderCell: (params) => <b style={{ fontStyle: 'italic' }}>{params.value}</b>
    },
    {
      field: 'coverage',
      headerName: 'Gene Coverage',
      flex: 1
    }
  ], []);

  function GridCustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExportContainer>
          <GridCsvExportMenuItem options={{ fileName: sample.attributes.sample_name + '_resistance_markers' }}/>
        </GridToolbarExportContainer>
      </GridToolbarContainer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper" >
      <DialogTitle sx={{ backgroundColor: "#eee" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h7">Complete Resistance Marker Profile</Typography>
          <Tooltip 
                placement="left-start"
                title={
                  <Typography fontSize={12}>
                    Resistance marker presence or absence is often not indicative of antibiotic resistance or susceptibility.
                    Refer to resistance genes reported alongside AMR predictions for highest predictive value.
                  </Typography>
                }
                arrow
            >
                <WarningIcon fontSize="medium" color='primary' sx={{ paddingLeft: 1, cursor: 'pointer' }} />
            </Tooltip>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ padding: 2, '& .even': { backgroundColor: '#fff' }, '& .odd': { backgroundColor: '#f5f5f5' } }}>
        {rows.length > 0 ? (
          <>
            <Typography paragraph sx={{ alignContent: 'center', marginBottom: 0 }}>
              The following table represents a complete profile of resistance markers identified in the analyzed sample.
            </Typography>
            {isPolymicrobial &&
              <Typography variant='body2' color='gray' sx={{fontStyle: 'italic'}}>
                Comprehensive resistance marker profiling for highly mixed samples is still under development.
                Please notify our team at pathosight-support@cepheid.com of any confusing or inconsistent results.
              </Typography>
            }
            {showKgASTQualityWarning && 
              <Alert severity="warning" sx={{ marginTop: 2 }}>
                {filterOutResGenesWithQualityLimitations ? "Some identified species do not have resistance markers displayed " : "Resistance markers are flagged high risk "}
                due to insufficient sequencing data or assembly quality.
                Caution recommended in interpreting resistance marker profiles.
              </Alert>
            }
            
            <DataGrid
              rows={rows}
              columns={columns}
              disableColumnSorting
              getRowHeight={() => 'auto'}
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
              slots={{ toolbar: GridCustomToolbar }}
              sx={{ marginTop: 2, maxHeight: '400px', [`& .${gridClasses.cell}`]: { py: 0.75 } }}
            />
            {rows.some(row => row.unclassifiedDuplicateMarker) && (
              <Typography variant='body2' color='gray' sx={{fontStyle: 'italic', marginTop: 1}}>
                {<b style={{ fontSize: '15px', fontStyle: 'bold', color: 'orange' }}>*</b>} Resistance marker cannot be definitively assigned to specified organism.
              </Typography>
            )}
          </>
          ) : (showKgASTQualityWarning ? <p>Resistance markers not displayed due to insufficient sequencing data or assembly quality.</p> : <p>No resistance markers found.</p>)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#eee" }}>
        <Button color="primary" sx={{ marginRight: 2 }} onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResistanceGenesDialog;
